import { Component, computed, effect, input, OnInit, output, Signal } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { filter, skip } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import log from 'loglevel';
import { MatIconModule } from '@angular/material/icon';


/**
 * Type to describe an option of a select list.
 */
export interface OptionModel {

  /**
   * Value of the option.
   */
  value: string;

  /**
   * Label to display for this option.
   */
  view: string;

  /**
   * If the option is disabled or not.
   */
  disabled?: boolean;
}


/**
 * Inputs for the {@link SelectListComponent|SelectListComponent}.
 */
export interface SelectListComponentInputs {

  /**
   * See [SelectListComponent.searchBar]{@link SelectListComponent#searchBar}
   */
  searchBar?: boolean;

  /**
   * See [SelectListComponent.label]{@link SelectListComponent#label}
   */
  label?: string | null;

  /**
   * Input with the name of the icons to display near the select list.
   */
  icons?: string | null;

  /**
   * See [SelectListComponent.optionList]{@link SelectListComponent#optionList}
   */
  optionList?: OptionModel[];

  /**
   * See [SelectListComponent.initialSelectedOption]{@link SelectListComponent#initialSelectedOption}
   */
  initialSelectedOption?: string | null;

  /**
   * Input that determines if the select list can have multiple selections.
   */
  multiple?: boolean;
}


/**
 * Component to display a select list with a set of given options.
 *
 * This should be used for any component that needs to display a select list.
 *
 * TODO: Ad a way of cleaning inputs values when the parent form is submited.
 * And also a way of setting values to this component from the parent
 * component.
 */
@Component({
  selector: 'select-list',
  imports: [MatSelectModule, MatFormFieldModule, ReactiveFormsModule, MatInputModule, MatIconModule],
  templateUrl: './select-list.component.html',
  styleUrl: './select-list.component.scss'
})
export class SelectListComponent implements OnInit {

  protected logger = log.getLogger('SelectList');


  /**
   * Input that determines if the search bar functionality is on or not.
   */
  searchBar = input(false);


  /**
   * Input with the label to place near the select list.
   */
  label = input<string | null>(null);


  /**
   * Input with the name of the icon to display near the select list.
   */
  icon = input<string | null>(null);


  /**
   * Input with the list of options to display in the select list.
   */
  optionList = input<OptionModel[]>([]);


  /**
   * Initial selected option.
   *
   * If not provided, the first option of the optionList will be selected.
   */
  initialSelectedOption = input<string | null>(null);


  /**
   * Input that determines if the select list can have multiple selections.
   */
  multiple = input(false);


  /**
   * Emits the selected option when it changes.
   */
  selectedOptionChanged = output<string>();


  /**
   * Emits the selected options when they change.
   */
  selectedOptionsChanged = output<string[]>();


  /**
   * Sets the selected option programmaticaly.
   *
   * Used when the value should change programmaticaly and not by user
   * interaction.
   *
   * @param value The new value to set in the select list. This means selecting
   *   the option whose 'value' property matches this value.
   */
  setValue(value: string) {
    this.selectedOption.setValue(value);
  };


  /**
   * Default values for the select list if no options are provided.
   *
   * This specially useful in children classes that override the default option
   * list.
   */
  public defaultVals: OptionModel[] = [
    {
      view: 'No provided options',
      value: 'unknown'
    }
  ];


  /**
   * List of options to display in the select list.
   *
   * optionList is not directly used because the option list may be needed to
   * be modified before displaying it (mainly to add default values). Because
   * optionList is an input, it cannot be modified. Hence, the computed signal
   * currentOptionList is used to store the actual list of options to display,
   *  wether it is the original received from the input or a modified version
   * with the class' defaults option list.
   *
   * @TODO: This should be a transform, not a computed signal.
   */
  public currentOptionList: Signal<OptionModel[]> = computed(() => {
    return this.optionList().length ? this.optionList() : this.defaultVals
  });


  /**
   * Reference to the effect that updates the currentOptionList when the
   * optionList input changes.
   */
  protected currentOptionListEffectRef = effect(() => this.onOptionListChanged());


  /**
   * From control for the text input field.
   *
   * This control holds the string the user types to filter the available
   * options.
   */
  public searchStringInput = new FormControl('');


  /**
   * Control for the selected option.
   *
   * This controls holds the current selected option.
   */
  public selectedOption = new FormControl<string | null>(null);


  /**
   * Control for the selected options.
   *
   * This controls holds the current selected options.
   */
  public selectedOptions = new FormControl<string[]>([]);


  /**
   * Angular initialization hook.
   *
   * Prepares the pipes for the search string input.
   */
  ngOnInit(): void {

    if (this.multiple()) {
      this.selectedOptions.valueChanges.pipe(
        skip(0),
        filter(value => value !== null),

      ).subscribe((value) => {
        this.logger.debug('Selected option changed:', value);
        this.selectedOptionsChanged.emit(value)
      })
    } else {
      this.selectedOption.valueChanges.pipe(
        skip(0),
        filter(value => value !== null),

      ).subscribe((value) => {
        this.logger.debug('Selected option changed:', value);
        this.selectedOptionChanged.emit(value)
      })
    }
  }


  /**
   * Handles updates on the optionList input.
   *
   * It takes care of setting the appropriate currentOptionList value and the
   * selected option.
   */

  onOptionListChanged() {
    if (this.initialSelectedOption()) {
      if (this.getViewByValue(this.initialSelectedOption()) === null) {
        throw new Error(`The initial selected option '${this.initialSelectedOption()}' is not in the option list`);
      }

      this.selectedOption.setValue(this.initialSelectedOption());
      return;
    }
  }


  /**
   * Tells if the given text matches the search string.
   */
  matchesSearch(text: string): boolean {
    const searchString = this.searchStringInput.value || '';
    if (!searchString) {
      return true;
    }

    return text.toLowerCase().includes(searchString.toLowerCase());
  }


  /**
   * Gets the view of the option using the value.
   */
  getViewByValue(value: string | null): string | null {

    let view = null;

    const match = this.currentOptionList().find((option) => option.value === value);

    if (match) {
      view = match.view;
    }

    return view;
  }
}
